import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { joyApi } from "../api/joyApi";
import { speak, speechAvailable } from "../hooks/useSpeech";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import JoyButton from "../components/JoyButton";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";

export default function LessonDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const lessonQ = useQuery({ queryKey: ["lesson", id], queryFn: () => joyApi.lesson(id) });

  if (lessonQ.isLoading) return <LoadingScreen />;
  if (lessonQ.isError)
    return (
      <AppShell>
        <TopBar title="Lição 📖" to="/lessons" />
        <ErrorState onRetry={lessonQ.refetch} />
      </AppShell>
    );

  const lesson = lessonQ.data;
  const words = lesson.words || [];
  const word = words[index] || {};
  const total = words.length;
  const canSpeak = speechAvailable();

  const goTo = (i) => {
    setRevealed(false);
    setIndex(Math.max(0, Math.min(total - 1, i)));
  };

  return (
    <AppShell>
      <TopBar
        title={`${lesson.icon} ${lesson.title_pt}`}
        to="/lessons"
        testId="lesson-topbar"
        right={
          <span className="text-xs font-black px-2 py-1 rounded-full bg-[#FFF3B0] text-[#8B6B00] whitespace-nowrap">
            ⭐{lesson.xp_reward}
          </span>
        }
      />

      <div className="flex items-center justify-between mb-2">
        <span data-testid="word-progress" className="font-bold text-[#8B7355] text-sm">
          Palavra {index + 1} de {total}
        </span>
      </div>
      <div className="h-3 rounded-full bg-[#FFE0EC] overflow-hidden mb-6">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${((index + 1) / total) * 100}%`, background: "linear-gradient(90deg,#FF69B4,#FFD700)" }}
        />
      </div>

      <button
        data-testid="flashcard"
        onClick={() => setRevealed((r) => !r)}
        className="joy-press w-full bg-white rounded-[28px] p-6 border-4 border-[#FFE0EC] relative overflow-hidden joy-slide-in"
        style={{ boxShadow: "0 12px 30px rgba(255,105,180,0.18)" }}
        key={index}
      >
        <span className="absolute top-3 left-4 text-lg joy-twinkle">✨</span>
        <span className="absolute top-3 right-4 text-lg joy-twinkle" style={{ animationDelay: "0.6s" }}>🌟</span>
        <div className="text-7xl mb-3 mt-2 joy-bob">{word.image}</div>
        <p data-testid="flashcard-en" className="text-4xl font-black text-[#FF69B4]">{word.en}</p>

        <div className="mt-4">
          {revealed ? (
            <p data-testid="flashcard-pt" className="text-2xl font-black text-[#5B8FB9] joy-pop">{word.pt}</p>
          ) : (
            <p className="text-[#8B7355] font-bold">👆 Toque para ver!</p>
          )}
        </div>
      </button>

      <div className="mt-4">
        <JoyButton
          variant="teal"
          full
          data-testid="listen-button"
          onClick={() => {
            if (!speak(word.en)) {
              /* graceful fallback handled below */
            }
          }}
        >
          🔊 Ouvir pronúncia
        </JoyButton>
        {!canSpeak && (
          <p className="text-xs text-center text-[#8B7355] font-bold mt-2">
            🔇 Áudio não disponível neste navegador. Leia em voz alta: <b>{word.en}</b>
          </p>
        )}
      </div>

      <div className="flex gap-3 mt-5">
        <JoyButton variant="white" className="flex-1" disabled={index === 0} onClick={() => goTo(index - 1)} data-testid="prev-word">
          ← Anterior
        </JoyButton>
        <JoyButton variant="white" className="flex-1" disabled={index === total - 1} onClick={() => goTo(index + 1)} data-testid="next-word">
          Próxima →
        </JoyButton>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-5 pb-1" data-testid="word-preview">
        {words.map((w, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`shrink-0 w-14 h-14 rounded-[16px] flex items-center justify-center text-2xl border-2 joy-press ${
              i === index ? "border-[#FF69B4] bg-[#FFE0EC]" : "border-[#FFEAD6] bg-white"
            }`}
          >
            {w.image}
          </button>
        ))}
      </div>

      <JoyButton
        variant="pink"
        full
        className="mt-6 text-lg"
        style={{ minHeight: 60 }}
        data-testid="start-quiz-button"
        onClick={() => nav(`/quiz/${lesson.id}`)}
      >
        🎮 Fazer o Quiz! 🌟
      </JoyButton>

      <div className="bg-white rounded-[22px] p-4 flex items-start gap-3 border-2 border-[#FFE0EC] mt-5">
        <span className="text-2xl">🦊</span>
        <p className="font-bold text-[#4A3728] text-sm leading-snug">
          <b>Dica da Joy!</b><br />Toque no botão de áudio para ouvir em inglês!
        </p>
      </div>
    </AppShell>
  );
}
